import LockIcon from "@mui/icons-material/Lock";
import PublicIcon from "@mui/icons-material/Public";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { GridColDef } from "@mui/x-data-grid";
import { WorkspaceVisibility } from "../../../types/workspace";
import { WorkspacesTableRowDefinition } from "../types";
import { ActionCell } from "./ActionCell/ActionCell";
import { ActionOption } from "./ActionCell/utils";

const boldHeaderStyle = {
  fontWeight: "bold",
  fontSize: "12px",
};

export const createWorkspacesColumns = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  rows,
  onActionClick,
  showOwner = false,
}: {
  rows: WorkspacesTableRowDefinition[];
  onActionClick: (
    action: ActionOption,
    row: WorkspacesTableRowDefinition
  ) => void;
  showOwner?: boolean;
}): GridColDef<(typeof rows)[number]>[] => {
  const baseColumns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
      renderHeader: (params) => (
        <Typography sx={boldHeaderStyle}>{params.colDef.headerName}</Typography>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      width: 450,
      flex: 1,
      minWidth: 200,
      renderHeader: (params) => (
        <Typography sx={boldHeaderStyle}>{params.colDef.headerName}</Typography>
      ),
    },
  ];

  const ownerColumn: GridColDef<(typeof rows)[number]>[] = [
    {
      field: "owner",
      headerName: "Owner",
      flex: 1,
      minWidth: 150,
      renderHeader: (params) => (
        <Typography sx={boldHeaderStyle}>{params.colDef.headerName}</Typography>
      ),
    },
  ];

  const additionalColumns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: "visibilityStatus",
      headerName: "Visibility Status",
      minWidth: 140,
      renderHeader: (params) => (
        <Typography sx={boldHeaderStyle}>{params.colDef.headerName}</Typography>
      ),
      renderCell: (params) => {
        const status = params.value?.toString().toLowerCase();

        let icon;
        let tooltipText;
        let bgColor;
        let statusText;

        if (status === WorkspaceVisibility.PRIVATE) {
          icon = <LockIcon sx={{ color: "white", fontSize: "16px" }} />;
          tooltipText = "Private - Only you have access to this project";
          bgColor = "#0057b2";
          statusText = "Private";
        } else {
          icon = <PublicIcon sx={{ color: "white", fontSize: "16px" }} />;
          tooltipText =
            "Public - Anyone in your organization can view this project";
          bgColor = "#388e3c";
          statusText = "Public";
        }

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              height: "100%",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                backgroundColor: bgColor,
                width: 26,
                height: 26,
                minWidth: 26,
                minHeight: 26,
              }}
            >
              <Tooltip title={tooltipText} arrow>
                {icon}
              </Tooltip>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {statusText}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "creationDate",
      headerName: "Creation Date",
      minWidth: 140,
      renderHeader: (params) => (
        <Typography sx={boldHeaderStyle}>{params.colDef.headerName}</Typography>
      ),
      renderCell: (params) => {
        const date = new Date(params.value);
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              textAlign: "left",
            }}
          >
            <Typography variant="body2">
              {date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {date.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "lastUpdate",
      headerName: "Last Updated",
      minWidth: 140,
      renderHeader: (params) => (
        <Typography sx={boldHeaderStyle}>{params.colDef.headerName}</Typography>
      ),
      renderCell: (params) => {
        const date = new Date(params.value);
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              textAlign: "left",
            }}
          >
            <Typography variant="body2">
              {date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              })}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {date.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 70,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return <ActionCell row={params.row} onActionClick={onActionClick} />;
      },
    },
  ];

  return [
    ...baseColumns,
    ...(showOwner ? ownerColumn : []),
    ...additionalColumns,
  ];
};
